import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CategoryOrTrade } from './useLineItemData';
import { LineItemFormValues, MarkupTier } from './useLineItemFormSchema';
import { useLineItemImageSubmit } from './useLineItemImageSubmit';
import { PricingTier } from '../types';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

interface UseLineItemSubmitProps {
  lineItemId?: string;
  selectedCategory: CategoryOrTrade | null;
  selectedTrade: CategoryOrTrade | null;
  selectedBrowseCategory?: string | null;
  pricingTiers: any[];
  imagePreviewUrls: string[];
}

// Define the pricing calculation result type
interface PricingCalculationResult {
  markup: number;
  margin: number;
  finalPrice: number;
  sourceName: 'custom' | 'category' | 'global';
  sourceLabel: string;
  applicableTier?: LineItemMarkupTier;
  categoryName?: string;
}

export const useLineItemSubmit = ({
  lineItemId,
  selectedCategory,
  selectedTrade,
  selectedBrowseCategory = null,
  pricingTiers,
  imagePreviewUrls
}: UseLineItemSubmitProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = !!lineItemId;
  const { uploadImages } = useLineItemImageSubmit();
  const { guardWrite } = useImpersonationGuard();

  const onSubmit = async (data: LineItemFormValues) => {
    try {
      if (!guardWrite('edit line items')) return;
      setIsLoading(true);
      console.log("Form data to submit:", data);

      // Process and upload images
      const processedImageUrls = await uploadImages(imagePreviewUrls);

      // Get auth.uid() for created_by field
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      const userId = userData.user?.id;
      
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Get user's company_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      const companyId = profileData.company_id;

      // Calculate pricing and STORE final_price
      const baseCost = data.base_cost || 0;
      const overrideMarkup = data.override_markup || false;
      
      let finalMarkupPercentage: number;
      let finalPricingSource: string;
      let finalPrice: number;

      if (overrideMarkup) {
        finalMarkupPercentage = data.custom_markup || 0;
        finalPricingSource = 'custom';
        finalPrice = baseCost * (1 + finalMarkupPercentage / 100);
      } else {
        // Calculate current pricing from tiers/category/global
        const pricingResult = await calculateCurrentPricing({
          amount: baseCost,
          categoryId: selectedCategory?.id,
          companyId,
          customTiers: data.custom_tiers as LineItemMarkupTier[],
          useCustomTiers: data.use_custom_tiers || false
        });
        
        finalMarkupPercentage = pricingResult.markup;
        finalPricingSource = pricingResult.sourceName;
        finalPrice = pricingResult.finalPrice;
      }

      // Create the lineItem object - NOW INCLUDING final_price
      const tagsArray = data.tags || [];
      
      const lineItem = {
        title: data.title,
        description: data.description || '',
        base_unit: data.base_unit,
        base_cost: baseCost,
        conversion_unit: data.conversion_unit || null,
        conversion_factor: data.conversion_factor || null,
        productivity_unit: data.productivity_unit || null,
        productivity_rate: data.productivity_rate || null,
        tags: tagsArray,
        is_active: data.is_active,
        image_urls: processedImageUrls,
        company_id: companyId,
        created_by: userId,
        category: selectedCategory ? selectedCategory.name : null,
        trade: selectedTrade ? selectedTrade.name : null,
        category_id: selectedCategory?.id || null,
        // Store pricing metadata INCLUDING final_price
        markup_percentage: finalMarkupPercentage,
        markup_source: finalPricingSource,
        final_price: finalPrice, // NOW STORING final_price
        last_price_calculation: new Date().toISOString(),
        // Store custom markup settings
        override_markup: overrideMarkup,
        custom_markup: data.custom_markup || 0,
        use_custom_tiers: data.use_custom_tiers || false,
        browse_category_id: selectedBrowseCategory ?? null
      };

      if (isEditMode && lineItemId) {
        // Get current values for audit trail
        const { data: currentData } = await supabase
          .from('internal_line_items')
          .select('base_cost, markup_percentage, markup_source, final_price')
          .eq('id', lineItemId)
          .single();

        // Update existing line item - NOW INCLUDING final_price and custom markup fields
        const { error } = await supabase
          .from('internal_line_items')
          .update({
            title: lineItem.title,
            description: lineItem.description,
            base_unit: lineItem.base_unit,
            base_cost: lineItem.base_cost,
            conversion_unit: lineItem.conversion_unit,
            conversion_factor: lineItem.conversion_factor,
            productivity_unit: lineItem.productivity_unit,
            productivity_rate: lineItem.productivity_rate,
            tags: lineItem.tags,
            is_active: lineItem.is_active,
            image_urls: lineItem.image_urls,
            category: lineItem.category,
            trade: lineItem.trade,
            category_id: lineItem.category_id,
            markup_percentage: lineItem.markup_percentage,
            markup_source: lineItem.markup_source,
            final_price: lineItem.final_price, // NOW STORING final_price
            last_price_calculation: lineItem.last_price_calculation,
            override_markup: lineItem.override_markup, // Store custom markup toggle
            custom_markup: lineItem.custom_markup, // Store custom markup value
            use_custom_tiers: lineItem.use_custom_tiers, // Store custom tiers toggle
            browse_category_id: lineItem.browse_category_id,
            updated_at: new Date().toISOString()
          })
          .eq('id', lineItemId);

        if (error) throw error;

        // Log pricing change if pricing data changed
        if (currentData && (
          currentData.base_cost !== lineItem.base_cost ||
          currentData.markup_percentage !== lineItem.markup_percentage ||
          currentData.markup_source !== lineItem.markup_source
        )) {
          await supabase.rpc('log_pricing_change', {
            p_line_item_id: lineItemId,
            p_change_trigger: 'form_edit',
            p_previous_base_cost: currentData.base_cost || 0,
            p_previous_markup_percentage: currentData.markup_percentage || 0,
            p_previous_markup_source: currentData.markup_source || 'global',
            p_previous_final_price: currentData.final_price || 0,
            p_new_base_cost: lineItem.base_cost,
            p_new_markup_percentage: lineItem.markup_percentage,
            p_new_markup_source: lineItem.markup_source,
            p_new_final_price: lineItem.final_price
          });
        }

        // Update pricing tiers
        await updatePricingTiers(lineItemId, pricingTiers);

        // Update custom markup tiers if enabled
        if (data.use_custom_tiers) {
          await updateMarkupTiers(lineItemId, data.custom_tiers || []);
        } else {
          await deleteMarkupTiers(lineItemId);
        }
        
      } else {
        // Create new line item - NOW INCLUDING final_price
        const { data: newLineItem, error: insertError } = await supabase
          .from('internal_line_items')
          .insert(lineItem)
          .select('id')
          .single();

        if (insertError) throw insertError;

        const newLineItemId = newLineItem.id;

        // Insert pricing tiers for the new item
        await updatePricingTiers(newLineItemId, pricingTiers);
        
        // Insert markup tiers if enabled
        if (data.use_custom_tiers && data.custom_tiers && data.custom_tiers.length > 0) {
          await updateMarkupTiers(newLineItemId, data.custom_tiers);
        }

        // Log initial pricing for new line item
        await supabase.rpc('log_pricing_change', {
          p_line_item_id: newLineItemId,
          p_change_trigger: 'initial_create',
          p_previous_base_cost: 0,
          p_previous_markup_percentage: 0,
          p_previous_markup_source: 'global',
          p_previous_final_price: 0,
          p_new_base_cost: lineItem.base_cost,
          p_new_markup_percentage: lineItem.markup_percentage,
          p_new_markup_source: lineItem.markup_source,
          p_new_final_price: lineItem.final_price
        });
      }

      toast.success('Line item saved successfully');
      navigate('/cost-center/internal-line-items');
    } catch (error) {
      console.error('Error saving line item:', error);
      toast.error(`Failed to save line item: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to calculate current pricing
  const calculateCurrentPricing = async ({
    amount,
    categoryId,
    companyId,
    customTiers = [],
    useCustomTiers = false
  }: {
    amount: number;
    categoryId?: string;
    companyId?: string;
    customTiers?: LineItemMarkupTier[];
    useCustomTiers?: boolean;
  }) => {
    try {
      // 1. Custom Tiers (highest priority)
      if (useCustomTiers && customTiers.length > 0) {
        const applicableTier = customTiers.find(tier => 
          amount >= tier.min_amount && 
          (tier.max_amount === null || amount < tier.max_amount)
        );
        
        if (applicableTier) {
          return {
            markup: applicableTier.markup_percentage,
            margin: applicableTier.margin_percentage,
            sourceName: 'custom',
            finalPrice: amount * (1 + applicableTier.markup_percentage / 100)
          };
        }
      }
      
      // 2. Category Tiers
      if (categoryId && companyId) {
        const { data: markupData } = await supabase
          .rpc('get_category_markup', { 
            p_category_id: categoryId,
            p_amount: amount,
            p_company_id: companyId
          });
          
        const { data: marginData } = await supabase
          .rpc('get_category_margin', { 
            p_category_id: categoryId,
            p_amount: amount,
            p_company_id: companyId
          });
        
        if (markupData !== null) {
          return {
            markup: markupData,
            margin: marginData || 0,
            sourceName: 'category',
            finalPrice: amount * (1 + markupData / 100)
          };
        }
      }
      
      // 3. Global Fallback
      if (companyId) {
        const { data: settings } = await supabase
          .from('cost_center_settings')
          .select('default_markup, default_margin')
          .eq('company_id', companyId)
          .maybeSingle();
          
        const markup = settings?.default_markup || 0;
        return {
          markup: markup,
          margin: settings?.default_margin || 0,
          sourceName: 'global',
          finalPrice: amount * (1 + markup / 100)
        };
      }
      
      return { 
        markup: 0, 
        margin: 0, 
        sourceName: 'global',
        finalPrice: amount 
      };
      
    } catch (err) {
      console.error('Error calculating pricing:', err);
      return { 
        markup: 0, 
        margin: 0, 
        sourceName: 'global',
        finalPrice: amount 
      };
    }
  };

  // Helper function for updating pricing tiers
  const updatePricingTiers = async (lineItemId: string, pricingTiers: PricingTier[]) => {
    try {
      const { error: deleteError } = await supabase
        .from('internal_pricing_tiers')
        .delete()
        .eq('line_item_id', lineItemId);

      if (deleteError) throw deleteError;
      
      if (pricingTiers && pricingTiers.length > 0) {
        const pricingTiersToInsert = pricingTiers.map(tier => ({
          line_item_id: lineItemId,
          qty_min: tier.qty_min,
          qty_max: tier.qty_max,
          unit_cost: tier.unit_cost,
          custom_markup: tier.custom_markup
        }));

        const { error: insertError } = await supabase
          .from('internal_pricing_tiers')
          .insert(pricingTiersToInsert);

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('Error updating pricing tiers:', error);
      throw error;
    }
  };
  
  // Helper function for updating markup tiers
  const updateMarkupTiers = async (lineItemId: string, markupTiers: MarkupTier[]) => {
    try {
      await deleteMarkupTiers(lineItemId);
      
      if (markupTiers && markupTiers.length > 0) {
        const markupTiersToInsert = markupTiers.map(tier => ({
          line_item_id: lineItemId,
          min_amount: tier.min_amount,
          max_amount: tier.max_amount,
          markup_percentage: tier.markup_percentage,
          margin_percentage: tier.margin_percentage
        }));

        const { error: insertError } = await supabase
          .from('line_item_markup_tiers')
          .insert(markupTiersToInsert);

        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('Error updating markup tiers:', error);
      throw error;
    }
  };
  
  // Helper function to delete existing markup tiers
  const deleteMarkupTiers = async (lineItemId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('line_item_markup_tiers')
        .delete()
        .eq('line_item_id', lineItemId);

      if (deleteError && deleteError.code !== 'PGRST116') {
        throw deleteError;
      }
    } catch (error) {
      console.error('Error deleting markup tiers:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate('/cost-center/internal-line-items');
  };

  return {
    isLoading,
    isEditMode,
    onSubmit,
    handleCancel
  };
};
